import { usePricing } from '@/contexts/PricingContext';
import { useSearchParams } from 'next/navigation';
import { useCallback, useState } from 'react';
import {
  applyOptionValuesToVariant,
  findVariantWithOptions,
  getDefaultVariant,
  getValidOptionsFromSearchParams,
  mergeShopifyOptions,
  restoreOptionCase,
  spreadOptionsObj,
} from './product-context-helpers';

/**
 * PDP selected variant + pricing (`PricingContext.variant`).
 *
 * Initial `selectedOptions` come from `getInitialVariantFromSearch` (CMS/Shopify default + URL keys valid for `shopifyProduct.options`). No post-mount sync from `searchParams`; URL and localStorage orchestration live in `ProductContext`.
 *
 * `mergeOptionsIntoState`: `restoreOptionCase` + `mergeShopifyOptions`; `prev` normalized with `?? []`.
 */
export default function useProductState(productData, defaultVariantId) {
  const pricing = usePricing();
  const searchParams = useSearchParams();

  const shopifyProduct = productData?.shopifyProduct;

  const initialVariant = getInitialVariantFromSearch(
    productData,
    defaultVariantId,
    searchParams,
  );

  const [selectedOptions, setSelectedOptions] = useState(
    initialVariant?.selectedOptions,
  );

  // Client-side nav reuses this instance across products (no remount) — reset
  // during render, not an effect, to skip the stale-variant frame.
  const [productKey, setProductKey] = useState(shopifyProduct?.id);
  if (shopifyProduct?.id !== productKey) {
    setProductKey(shopifyProduct?.id);
    setSelectedOptions(initialVariant?.selectedOptions);
  }

  const mergeOptionsIntoState = useCallback(
    (options) => {
      setSelectedOptions((prevOptions) => {
        const incomingOptions = restoreOptionCase(
          productData.shopifyProduct,
          options,
        );

        return mergeShopifyOptions(prevOptions ?? [], incomingOptions);
      });
    },
    [productData],
  );

  const selectedVariant = findVariantWithOptions(shopifyProduct, selectedOptions);

  return {
    mergeOptionsIntoState,
    selectedVariant: selectedVariant ?? initialVariant,
    selectedVariantPrices: pricing.variant(selectedVariant),
  };
}

/**
 * Initial variant: `getValidOptionsFromSearchParams(searchParams)` applied over
 * `getDefaultVariant` `selectedOptions` via `applyOptionValuesToVariant`
 * (case-insensitive, per-option). Nothing applied → `defaultVariant` + `console.warn`.
 */
function getInitialVariantFromSearch(productData, defaultVariantId, searchParams) {
  const defaultVariant = getDefaultVariant(productData, defaultVariantId);
  const sp = productData?.shopifyProduct;
  if (!sp?.options?.length) return defaultVariant;

  const fromUrl = getValidOptionsFromSearchParams(sp, searchParams);
  if (!fromUrl.length) return defaultVariant;

  // Query params arrive lowercase; `applyOptionValuesToVariant` resolves each
  // one against Shopify's real casing and only keeps values that land on a real
  // variant, so a partially-invalid URL still applies the options that do match.
  const { appliedOptions, selectedVariant: found } = applyOptionValuesToVariant(
    sp,
    defaultVariant?.selectedOptions ?? [],
    spreadOptionsObj(fromUrl),
  );
  if (appliedOptions.length && found?.selectedOptions) return found;

  console.warn(
    'Invalid option combination in URL. Available options are:',
    sp.options.map((o) => o.name.toLowerCase()).join(', '),
  );
  return defaultVariant;
}
